import React from 'react';
import {
  Box,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  Tooltip
} from '@chakra-ui/react';
import { FiVolume2, FiVolumeX } from 'react-icons/fi';
import { useSound } from '../../contexts/SoundContext.tsx';
import { useTranslation } from 'react-i18next';

interface SoundControlProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'compact' | 'full';
  iconColor?: string;
}

export const SoundControl: React.FC<SoundControlProps> = ({
  size = 'md',
  variant = 'compact',
  iconColor
}) => {
  const { t } = useTranslation();
  const {
    masterVolume,
    setMasterVolume,
    isMuted,
    toggleMute,
    soundEnabled,
    setSoundEnabled,
    playSound
  } = useSound();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.300');

  const handleVolumeChange = (value: number) => {
    setMasterVolume(value / 100);
    // Ne plus jouer de son automatiquement lors du changement de volume pour éviter le spam
  };

  const handleToggleMute = () => {
    toggleMute();
    // Jouer un son de confirmation
    if (!isMuted && soundEnabled) {
      playSound('buttonClick');
    }
  };

  const handleToggleSound = () => {
    setSoundEnabled(!soundEnabled);
    if (soundEnabled) {
      playSound('buttonClick');
    }
  };

  if (variant === 'compact') {
    return (
      <Popover placement="bottom-end">
        <PopoverTrigger>
          <IconButton
            aria-label={t('sound.settings')}
            icon={soundEnabled ? <FiVolume2 /> : <FiVolumeX />}
            size={size}
            variant="ghost"
            color={iconColor}
            _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          />
        </PopoverTrigger>
        <PopoverContent
          bg={bgColor}
          borderColor={borderColor}
          width={{ base: "90vw", sm: "320px", md: "280px" }}
          maxWidth="400px"
          boxShadow="xl"
          mx={{ base: 2, md: 0 }}
        >
          <PopoverBody p={{ base: 3, md: 4 }}>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              <HStack justify="space-between">
                <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium" color={textColor}>
                  {t('sound.title')}
                </Text>
                <Tooltip label={soundEnabled ? t('sound.disable') : t('sound.enable')}>
                  <IconButton
                    aria-label={soundEnabled ? t('sound.disable') : t('sound.enable')}
                    icon={soundEnabled ? <FiVolume2 /> : <FiVolumeX />}
                    size="sm"
                    variant="ghost"
                    onClick={handleToggleSound}
                    color={soundEnabled ? 'green.500' : 'red.500'}
                  />
                </Tooltip>
              </HStack>

              {soundEnabled && (
                <>
                  <HStack spacing={{ base: 2, md: 3 }}>
                    <Tooltip label={isMuted ? t('sound.unmute') : t('sound.mute')}>
                      <IconButton
                        aria-label={isMuted ? t('sound.unmute') : t('sound.mute')}
                        icon={isMuted ? <FiVolumeX /> : <FiVolume2 />}
                        size="sm"
                        variant="ghost"
                        onClick={handleToggleMute}
                        color={isMuted ? 'red.500' : textColor}
                      />
                    </Tooltip>
                    <Box flex={1}>
                      <Slider
                        value={masterVolume * 100}
                        onChange={handleVolumeChange}
                        min={0}
                        max={100}
                        step={5}
                        isDisabled={isMuted || !soundEnabled}
                        colorScheme="purple"
                      >
                        <SliderTrack>
                          <SliderFilledTrack />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>
                    <Text fontSize={{ base: "xs", md: "sm" }} color={textColor} minWidth="35px">
                      {Math.round(masterVolume * 100)}%
                    </Text>
                  </HStack>

                  <Box>
                    <Text fontSize="xs" color={textColor} mb={2}>
                      {t('sound.testSounds')}
                    </Text>
                    <HStack spacing={2} wrap="wrap">
                      <IconButton
                        aria-label={t('sound.test.ui')}
                        size="xs"
                        onClick={() => playSound('buttonClick')}
                        variant="outline"
                        isDisabled={isMuted || !soundEnabled}
                      >
                        UI
                      </IconButton>
                      <IconButton
                        aria-label={t('sound.test.correct')}
                        size="xs"
                        onClick={() => playSound('answerCorrect')}
                        variant="outline"
                        colorScheme="green"
                        isDisabled={isMuted || !soundEnabled}
                      >
                        ✓
                      </IconButton>
                      <IconButton
                        aria-label={t('sound.test.wrong')}
                        size="xs"
                        onClick={() => playSound('answerWrong')}
                        variant="outline"
                        colorScheme="red"
                        isDisabled={isMuted || !soundEnabled}
                      >
                        ✗
                      </IconButton>
                      <IconButton
                        aria-label={t('sound.test.category')}
                        size="xs"
                        onClick={() => playSound('categorySelect')}
                        variant="outline"
                        colorScheme="purple"
                        isDisabled={isMuted || !soundEnabled}
                      >
                        🎵
                      </IconButton>
                    </HStack>
                  </Box>
                </>
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );
  }

  // Version complète pour les paramètres
  return (
    <Box
      bg={bgColor}
      borderColor={borderColor}
      borderWidth={1}
      borderRadius="lg"
      p={6}
      width="100%"
      maxWidth="400px"
    >
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            {t('sound.title')}
          </Text>
          <Tooltip label={soundEnabled ? t('sound.disable') : t('sound.enable')}>
            <IconButton
              aria-label={soundEnabled ? t('sound.disable') : t('sound.enable')}
              icon={soundEnabled ? <FiVolume2 /> : <FiVolumeX />}
              size="md"
              variant="ghost"
              onClick={handleToggleSound}
              color={soundEnabled ? 'green.500' : 'red.500'}
            />
          </Tooltip>
        </HStack>

        {soundEnabled && (
          <>
            <VStack spacing={4}>
              <HStack spacing={4} width="100%">
                <Tooltip label={isMuted ? t('sound.unmute') : t('sound.mute')}>
                  <IconButton
                    aria-label={isMuted ? t('sound.unmute') : t('sound.mute')}
                    icon={isMuted ? <FiVolumeX /> : <FiVolume2 />}
                    size="md"
                    variant="ghost"
                    onClick={handleToggleMute}
                    color={isMuted ? 'red.500' : textColor}
                  />
                </Tooltip>
                <Box flex={1}>
                  <Text fontSize="sm" color={textColor} mb={2}>
                    {t('sound.volume')}: {Math.round(masterVolume * 100)}%
                  </Text>
                  <Slider
                    value={masterVolume * 100}
                    onChange={handleVolumeChange}
                    min={0}
                    max={100}
                    step={5}
                    isDisabled={isMuted || !soundEnabled}
                    colorScheme="purple"
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </Box>
              </HStack>
            </VStack>

            <Box>
              <Text fontSize="sm" fontWeight="medium" color={textColor} mb={3}>
                {t('sound.testSounds')}
              </Text>
              <HStack spacing={3} wrap="wrap">
                <IconButton
                  aria-label={t('sound.test.ui')}
                  onClick={() => playSound('buttonClick')}
                  variant="outline"
                  isDisabled={isMuted || !soundEnabled}
                >
                  {t('sound.test.ui')}
                </IconButton>
                <IconButton
                  aria-label={t('sound.test.correct')}
                  onClick={() => playSound('answerCorrect')}
                  variant="outline"
                  colorScheme="green"
                  isDisabled={isMuted || !soundEnabled}
                >
                  {t('sound.test.correct')}
                </IconButton>
                <IconButton
                  aria-label={t('sound.test.wrong')}
                  onClick={() => playSound('answerWrong')}
                  variant="outline"
                  colorScheme="red"
                  isDisabled={isMuted || !soundEnabled}
                >
                  {t('sound.test.wrong')}
                </IconButton>
                <IconButton
                  aria-label={t('sound.test.victory')}
                  onClick={() => playSound('gameWin')}
                  variant="outline"
                  colorScheme="yellow"
                  isDisabled={isMuted || !soundEnabled}
                >
                  {t('sound.test.victory')}
                </IconButton>
              </HStack>
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
};
